import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import ArticleCard from './ArticleCard';
import styles from './FeaturedSection.module.css';

export default function FeaturedSection({ articles = [], trending = [] }) {
  // Filtro defensivo: si algún artículo llega sin slug (bug del backend
  // o mock), lo excluimos para evitar links rotos que caen en 404.
  const validArticles = articles.filter(a => a && a.slug);
  if (validArticles.length === 0) return null;

  // Prioriza los destacados con imagen para el mainArticle. Los que no
  // tienen imagen quedan igual disponibles pero se muestran después,
  // con un layout tipo "opinion" (título grande, sin caja rota).
  const withImage = validArticles.filter(a => a.image);
  const withoutImage = validArticles.filter(a => !a.image);
  const ordered = [...withImage, ...withoutImage];

  const mainArticle = ordered[0];
  const secondaryArticles = ordered.slice(1, 3);
  const sideArticles = ordered.slice(3, 6);

  return (
    <section className={styles.featured}>
      <div className="container">
        {/* Título del periódico con fecha */}
        <div className={styles.masthead}>
          <div className={styles.mastheadDecoration}>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <h1 className={styles.mastheadTitle}>Última Hora</h1>
          <p className={styles.mastheadDate}>
            {new Date().toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        <div className={styles.grid}>
          {/* Columna principal */}
          <div className={styles.mainColumn}>
            {/* Artículo principal destacado */}
            <article className={styles.mainArticle}>
              {mainArticle.image ? (
                <Link to={`/articulo/${mainArticle.slug}`} className={styles.mainImageWrapper}>
                  <img
                    src={mainArticle.image}
                    alt={mainArticle.title}
                    className={styles.mainImage}
                  />
                  {mainArticle.category && (
                    <span
                      className={styles.mainCategory}
                      style={{ backgroundColor: mainArticle.category?.color }}
                    >
                      {mainArticle.category?.name}
                    </span>
                  )}
                </Link>
              ) : (
                mainArticle.category && (
                  <Link
                    to={`/seccion/${mainArticle.category.slug}`}
                    className={styles.mainCategoryStandalone}
                    style={{ color: mainArticle.category.color }}
                  >
                    {mainArticle.category.name}
                  </Link>
                )
              )}
              <div className={styles.mainContent}>
                <h2 className={styles.mainTitle}>
                  <Link to={`/articulo/${mainArticle.slug}`}>
                    {mainArticle.title}
                  </Link>
                </h2>
                {mainArticle.subtitle && (
                  <p className={styles.mainSubtitle}>{mainArticle.subtitle}</p>
                )}
                <p className={styles.mainExcerpt}>{mainArticle.excerpt}</p>
                <div className={styles.mainMeta}>
                  <span className={styles.mainAuthor}>
                    Por <Link to={`/autor/${mainArticle.author?.id}`}>
                      {mainArticle.author?.name}
                    </Link>
                  </span>
                </div>
              </div>
            </article>

            {/* Artículos secundarios */}
            {secondaryArticles.length > 0 && (
              <div className={styles.secondaryGrid}>
                {secondaryArticles.map(article => (
                  <ArticleCard 
                    key={article.id} 
                    article={article}
                    showExcerpt={true}
                    imageAspect="16/10"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Columna lateral */}
          <aside className={styles.sidebar}>
            {/* Trending */}
            <div className={styles.trendingSection}>
              <h3 className={styles.sidebarTitle}>
                <TrendingUp size={18} />
                Lo más leído
              </h3>
              <ol className={styles.trendingList}>
                {trending.slice(0, 5).map((article, index) => (
                  <li key={article.id} className={styles.trendingItem}>
                    <span className={styles.trendingNumber}>{index + 1}</span>
                    <div className={styles.trendingContent}>
                      <Link 
                        to={`/seccion/${article.category?.slug}`}
                        className={styles.trendingCategory}
                        style={{ color: article.category?.color }}
                      >
                        {article.category?.name}
                      </Link>
                      <h4 className={styles.trendingTitle}>
                        <Link to={`/articulo/${article.slug}`}>
                          {article.title}
                        </Link>
                      </h4>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Más noticias */}
            {sideArticles.length > 0 && (
              <div className={styles.moreSection}>
                <h3 className={styles.sidebarTitle}>Más noticias</h3>
                <div className={styles.moreList}>
                  {sideArticles.map(article => (
                    <ArticleCard 
                      key={article.id}
                      article={article}
                      variant="compact"
                      showImage={false}
                      showExcerpt={false}
                      showMeta={false}
                    />
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
}
